/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import { Typography, Box, Button, Collapse, IconButton } from '@mui/material';
import { DownloadIcons, ShareIcons, UtmCloseIcons } from '../../icons';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'react-qr-code';

export default function QrCodeModal({ onClose, open, link, campaignName }) {
    const qrRef = React.useRef(null);
    const [showShare, setShowShare] = React.useState(false);

    // Reset share panel when modal closes
    React.useEffect(() => {
        if (!open) {
            setShowShare(false);
        }
    }, [open]);

    const qrValue = link && link !== '-' ? link : 'https://www.favordating.com';

    const handleDownload = () => {
        const svg = qrRef.current?.querySelector('svg');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            // Add padding for the border effect
            const padding = 20;
            canvas.width = img.width + padding * 2;
            canvas.height = img.height + padding * 2;

            // Draw white background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw border
            ctx.strokeStyle = '#916008';
            ctx.lineWidth = 5;
            ctx.strokeRect(2.5, 2.5, canvas.width - 5, canvas.height - 5);

            // Draw QR code
            ctx.drawImage(img, padding, padding);

            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.href = pngFile;
            downloadLink.download = `qr-code-${campaignName || 'campaign'}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    const shareText = campaignName
        ? `Check out this campaign: ${campaignName} - ${qrValue}`
        : `Check out this link: ${qrValue}`;

    const shareLinks = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(qrValue)}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(qrValue)}&text=${encodeURIComponent(shareText)}`,
        instagram: `https://www.instagram.com/`, // Instagram doesn't support direct URL sharing
        whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
        pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(qrValue)}&description=${encodeURIComponent(shareText)}`,
    };

    const handleShare = (platform) => {
        const url = shareLinks[platform];
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
        }
    };

    return (
        <React.Fragment>

            <Dialog
                open={open}
                onClose={onClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{
                    '& .MuiPaper-root': {
                        padding: '20px 14px',
                        width: '380px',
                        borderRadius: '7px'
                    }
                }}
            >
                <Box>
                    <Box sx={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px' }}>
                        <Box>
                            <Typography sx={{ color: '#101828', fontWeight: '500', fontSize: '18px', fontFamily: 'Neue Montreal' }}>QR Code</Typography>
                            {campaignName && (
                                <Typography sx={{ color: '#4A5565', fontWeight: '400', fontSize: '13px', fontFamily: 'Neue Montreal' }}>
                                    {campaignName}
                                </Typography>
                            )}
                        </Box>
                        <UtmCloseIcons onClick={onClose} />
                    </Box>

                    <Box
                        ref={qrRef}
                        sx={{
                            border: '5px solid #916008',
                            borderRadius: '12px',
                            width: '240px',
                            height: '252px',
                            marginBottom: '20px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            margin: 'auto',
                            background: '#fff',
                            padding: '10px'
                        }}
                    >
                        <QRCode
                            value={qrValue}
                            size={210}
                            level="H"
                            fgColor="#000000"
                            bgColor="#ffffff"
                        />
                    </Box>

                    {/* Show the link below QR */}
                    <Box sx={{ marginTop: '12px', textAlign: 'center' }}>
                        <Typography
                            sx={{
                                color: '#2E80DB',
                                fontWeight: '400',
                                fontSize: '12px',
                                fontFamily: 'Neue Montreal',
                                wordBreak: 'break-all',
                                padding: '0 10px'
                            }}
                        >
                            {qrValue}
                        </Typography>
                    </Box>

                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px'
                    }}>
                        <Button onClick={() => setShowShare(prev => !prev)} sx={{ border: '1px solid #ddd', borderRadius: '7px', width: '100%', color: '#000', fontSize: '14px', fontWeight: '400', textTransform: 'capitalize', fontFamily: 'Neue Montreal', padding: '11px' }}>
                            Share <ShareIcons style={{ marginLeft: '6px' }} />
                        </Button>
                        <Button onClick={handleDownload} sx={{ border: '1px solid #ddd', borderRadius: '7px', width: '100%', color: '#000', fontSize: '14px', fontWeight: '400', textTransform: 'capitalize', fontFamily: 'Neue Montreal', padding: '11px' }}>
                            Download <DownloadIcons style={{ marginLeft: '6px' }} />
                        </Button>
                    </Box>

                    <Collapse in={showShare} timeout={400}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '12px',
                                marginTop: '12px',
                                position: 'relative'
                            }}
                        >
                            {/* Close button */}
                            <IconButton
                                onClick={() => setShowShare(false)}
                                sx={{ position: 'absolute', right: -10, top: -10 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleShare('facebook'); }}>
                                <Box component='img' src='/assets/images/admin-panel/facebookicon.png' sx={{ height: '32px', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
                            </a>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleShare('telegram'); }}>
                                <Box component='img' src='/assets/images/admin-panel/telegramicon.png' sx={{ height: '32px', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
                            </a>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleShare('instagram'); }}>
                                <Box component='img' src='/assets/images/admin-panel/instagram-icon.png' sx={{ height: '32px', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
                            </a>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleShare('whatsapp'); }}>
                                <Box component='img' src='/assets/images/admin-panel/whatsappicon.png' sx={{ height: '32px', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
                            </a>
                            <a href="#" onClick={(e) => { e.preventDefault(); handleShare('pinterest'); }}>
                                <Box component='img' src='/assets/images/admin-panel/pintrestt.png' sx={{ height: '32px', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
                            </a>

                        </Box>
                    </Collapse>
                </Box>
            </Dialog>
        </React.Fragment>
    );
}
